import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { Segmented } from './Segmented';
import { UserRound, Briefcase } from './icons';
import { FondNuit } from './FondNuit';
import { colors, formes, gradients, spacing } from '../theme/tokens';

export interface InterlocuteurOption<T extends string> {
  value: T;
  label: string;
  // Thèmes abordés, sur une ligne : « Motivation · Parcours · Compétences ».
  tags: string;
}

export interface CarteInterlocuteurProps<T extends string> {
  options: InterlocuteurOption<T>[];
  value: T;
  onChange: (value: T) => void;
  testID?: string;
}

// Hauteurs des barres (en points), tirées de la maquette : une silhouette de voix, pas une mesure.
const BARRES = [10, 18, 26, 14, 32, 22, 12, 28, 36, 20, 14, 30, 24, 34, 16, 26, 38, 22, 12, 28, 32, 18, 24, 14];

/**
 * Carte d'ouverture de la préparation d'entretien : l'interlocuteur choisi (avatar, nom, thèmes),
 * une onde sonore décorative, et le choix Recruteur / Manager en onglets. L'onde et le halo de
 * l'avatar sont immobiles ; la maquette les anime, ce qui viendra avec l'option « réduire les
 * animations ».
 */
export function CarteInterlocuteur<T extends string>({ options, value, onChange, testID }: CarteInterlocuteurProps<T>) {
  const choisi = options.find((o) => o.value === value) ?? options[0];
  const Icone = choisi.value === options[0].value ? UserRound : Briefcase;
  return (
    <FondNuit testID={testID} style={styles.carte}>
      <View style={styles.contenu}>
        <View style={styles.entete}>
          <View
            testID={testID ? `${testID}-avatar` : undefined}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={styles.avatarZone}
          >
            <View style={styles.halo} />
            <LinearGradient colors={gradients.voixEntretien} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.avatar}>
              <Icone size={30} color={colors.white} strokeWidth={2} />
            </LinearGradient>
          </View>
          <View style={styles.textes}>
            <Text testID={testID ? `${testID}-titre` : undefined} style={styles.titre}>
              {choisi.label}
            </Text>
            <Text style={styles.tags}>{choisi.tags}</Text>
          </View>
        </View>

        <View
          testID={testID ? `${testID}-son` : undefined}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={styles.son}
        >
          {BARRES.map((h, i) => (
            <LinearGradient key={i} colors={gradients.sonEntretien} style={[styles.barre, { height: h }]} />
          ))}
        </View>

        <Segmented
          surFond="sombre"
          options={options.map((o) => ({ key: o.value, label: o.label, testID: testID ? `${testID}-${o.value}` : undefined }))}
          value={value}
          onChange={(k) => onChange(k as T)}
        />
      </View>
    </FondNuit>
  );
}

const styles = StyleSheet.create({
  carte: { borderRadius: formes.rayon.lg },
  contenu: { padding: spacing.xl, gap: spacing.xl },
  entete: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  avatarZone: { width: 68, height: 68, alignItems: 'center', justifyContent: 'center' },
  halo: { position: 'absolute', width: 68, height: 68, borderRadius: 34, borderWidth: 2, borderColor: colors.roseVoix, opacity: 0.6 },
  avatar: { width: 60, height: 60, borderRadius: 30, alignItems: 'center', justifyContent: 'center' },
  textes: { flex: 1, gap: 3 },
  titre: { fontSize: 20, fontWeight: '800', color: colors.white },
  tags: { fontSize: 13, lineHeight: 17, color: colors.blancAtenue },
  son: { height: 44, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  barre: { width: 3.5, borderRadius: 2 },
});
